import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

// ─── helpers ────────────────────────────────────────────────────────────────

function copyText(text) {
  navigator.clipboard?.writeText(text).catch(() => {});
}

function getBranchLabel(orderValue, steps) {
  if (orderValue == null) return 'next step';
  const target = steps.find(s => s.order === orderValue);
  return target ? target.tool_instance_name : `Step order ${orderValue}`;
}

// ─── StepCard ───────────────────────────────────────────────────────────────

function StepCard({
  step, index, steps, isExpanded, onToggle, onRemove, onChange,
  toolTypeMetadata, provided, snapshot,
}) {
  const isConditional = step.tool_type_code === 'conditional';
  const mappingCount = Object.keys(step.input_mapping || {}).length;

  function handleMappingAdd() {
    const existing = Object.keys(step.input_mapping || {});
    let key = 'key';
    let n = 1;
    while (existing.includes(key)) key = `key${n++}`;
    onChange(index, 'input_mapping', { ...(step.input_mapping || {}), [key]: '' });
  }

  function handleMappingKeyChange(oldKey, newKey) {
    const m = { ...(step.input_mapping || {}) };
    const val = m[oldKey];
    delete m[oldKey];
    m[newKey] = val;
    onChange(index, 'input_mapping', m);
  }

  function handleMappingValueChange(key, value) {
    onChange(index, 'input_mapping', { ...(step.input_mapping || {}), [key]: value });
  }

  function handleMappingDelete(key) {
    const m = { ...(step.input_mapping || {}) };
    delete m[key];
    onChange(index, 'input_mapping', m);
  }

  // Build variable chips for prior steps
  const priorVariables = [];
  for (let pi = 0; pi < index; pi++) {
    const ps = steps[pi];
    const schema = toolTypeMetadata[ps.tool_type_code] || {};
    Object.keys(schema).forEach(field => {
      priorVariables.push({
        label: `{{step_${ps.order}.${field}}}`,
        title: schema[field],
        stepLabel: `Step ${pi + 1}`,
      });
    });
  }

  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      className={`step-card ${snapshot.isDragging ? 'dragging' : ''} ${isExpanded ? 'step-card-expanded' : ''}`}
    >
      {/* ── header ── */}
      <div className="step-header" {...provided.dragHandleProps}>
        <span className="drag-handle"><i className="fas fa-grip-vertical" /></span>

        <div className="step-header-info">
          <span className="step-number">Step {index + 1}</span>
          <span className="step-tool-name">{step.tool_instance_name}</span>
          <span className={`step-type-badge step-type-${step.tool_type_code}`}>{step.tool_type_name}</span>
        </div>

        {/* collapsed summary badges */}
        {!isExpanded && (
          <div className="step-collapsed-badges">
            {mappingCount > 0 && (
              <span className="badge-mapping" title={`${mappingCount} input mapping(s) defined`}>
                <i className="fas fa-link" /> {mappingCount}
              </span>
            )}
            {isConditional && (step.branch_true != null || step.branch_false != null) && (
              <span className="badge-branch" title="Conditional branching configured">
                <i className="fas fa-code-branch" />
              </span>
            )}
          </div>
        )}

        <div className="step-header-actions">
          <button type="button" className="btn-step-toggle" onClick={() => onToggle(index)}
            title={isExpanded ? 'Collapse' : 'Expand'}>
            <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} />
          </button>
          <button type="button" className="btn-remove" onClick={() => onRemove(index)}>
            <i className="fas fa-times" />
          </button>
        </div>
      </div>

      {/* ── expanded body ── */}
      {isExpanded && (
        <div className="step-expanded-body">

          {/* INPUT MAPPING */}
          <div className="step-section">
            <div className="step-section-title">
              <i className="fas fa-arrow-right-to-bracket" /> Input Mapping
              <span className="step-section-hint">
                {mappingCount === 0
                  ? 'Passing previous step output unchanged'
                  : `${mappingCount} mapping${mappingCount !== 1 ? 's' : ''} defined`}
              </span>
            </div>

            {/* available variables */}
            <div className="variable-chips">
              <span className="chips-label">Available:</span>
              <button type="button" className="var-chip var-chip-input"
                onClick={() => copyText('{{input.*}}')} title="Initial pipeline input">
                {'{{input.*}}'}
              </button>
              {priorVariables.map((v, vi) => (
                <button key={vi} type="button" className="var-chip var-chip-step"
                  onClick={() => copyText(v.label)} title={`${v.stepLabel}: ${v.title}`}>
                  {v.label}
                </button>
              ))}
              {priorVariables.length === 0 && index > 0 && (
                <span className="chips-hint">Prior steps have no schema info</span>
              )}
            </div>
            <p className="chips-copy-hint">Click a variable to copy it to your clipboard.</p>

            {/* mapping rows */}
            <div className="mapping-editor">
              {Object.entries(step.input_mapping || {}).map(([key, value], mi) => (
                <div key={mi} className="mapping-row">
                  <input className="mapping-key" value={key} placeholder="input_key"
                    onChange={e => handleMappingKeyChange(key, e.target.value)} />
                  <span className="mapping-sep">=</span>
                  <input className="mapping-value" value={value} placeholder="{{step_0.text}} or literal value"
                    onChange={e => handleMappingValueChange(key, e.target.value)} />
                  <button type="button" className="btn-mapping-delete"
                    onClick={() => handleMappingDelete(key)} title="Remove">
                    <i className="fas fa-times" />
                  </button>
                </div>
              ))}
              <button type="button" className="btn-add-mapping" onClick={handleMappingAdd}>
                <i className="fas fa-plus" /> Add mapping
              </button>
            </div>
          </div>

          {/* BRANCHING — conditional steps only */}
          {isConditional && (
            <div className="step-section branch-section">
              <div className="step-section-title">
                <i className="fas fa-code-branch" /> Branching
                <span className="step-section-hint">Where to go based on this condition's result</span>
              </div>
              <div className="branch-config">
                <div className="branch-row">
                  <span className="branch-label branch-true"><i className="fas fa-check" /> If true:</span>
                  <select
                    value={step.branch_true ?? ''}
                    onChange={e => onChange(index, 'branch_true', e.target.value === '' ? null : parseInt(e.target.value))}
                  >
                    <option value="">Continue to next step</option>
                    {steps.map((s, si) => si !== index && (
                      <option key={si} value={s.order}>
                        Step {si + 1}: {s.tool_instance_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="branch-row">
                  <span className="branch-label branch-false"><i className="fas fa-times" /> If false:</span>
                  <select
                    value={step.branch_false ?? ''}
                    onChange={e => onChange(index, 'branch_false', e.target.value === '' ? null : parseInt(e.target.value))}
                  >
                    <option value="">Continue to next step</option>
                    {steps.map((s, si) => si !== index && (
                      <option key={si} value={s.order}>
                        Step {si + 1}: {s.tool_instance_name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ERROR HANDLING */}
          <div className="step-section step-section-inline">
            <div className="step-section-title">
              <i className="fas fa-triangle-exclamation" /> On Error
            </div>
            <select className="on-error-select"
              value={step.on_error}
              onChange={e => onChange(index, 'on_error', e.target.value)}>
              <option value="stop">Stop execution</option>
              <option value="skip">Skip and continue</option>
              <option value="retry">Retry once</option>
            </select>
          </div>

        </div>
      )}

      {/* connector arrow */}
      {index < steps.length - 1 && !snapshot.isDragging && (
        <div className="step-connector">
          {isConditional && (step.branch_true != null || step.branch_false != null)
            ? <span className="step-connector-branch"><i className="fas fa-code-branch" /> branches</span>
            : <i className="fas fa-arrow-down" />}
        </div>
      )}
    </div>
  );
}

// ─── ResultViewer ────────────────────────────────────────────────────────────

function ResultViewer({ result }) {
  const [activeTab, setActiveTab] = useState('summary');

  if (!result) return null;

  const steps = result.results || [];
  const succeeded = steps.filter(s => s.result?.success).length;

  return (
    <div className={`result-viewer ${result.success ? 'result-success' : 'result-error'}`}>
      <div className="result-header">
        <i className={`fas ${result.success ? 'fa-check-circle' : 'fa-times-circle'}`} />
        <span>{result.success ? 'Execution succeeded' : 'Execution failed'}</span>
        {result.error && <span className="result-error-msg">{result.error}</span>}
        <div className="result-tabs">
          <button className={activeTab === 'summary' ? 'active' : ''} onClick={() => setActiveTab('summary')}>
            Steps ({succeeded}/{steps.length})
          </button>
          <button className={activeTab === 'output' ? 'active' : ''} onClick={() => setActiveTab('output')}>
            Final Output
          </button>
          <button className={activeTab === 'raw' ? 'active' : ''} onClick={() => setActiveTab('raw')}>
            Raw
          </button>
        </div>
      </div>

      {activeTab === 'summary' && (
        <div className="result-steps">
          {steps.map((s, i) => (
            <div key={i} className={`result-step ${s.result?.success ? 'ok' : 'fail'}`}>
              <div className="result-step-header">
                <i className={`fas ${s.result?.success ? 'fa-check' : 'fa-times'}`} />
                <span className="result-step-label">Step {s.step + 1}: {s.tool}</span>
              </div>
              {!s.result?.success && s.result?.error && (
                <div className="result-step-error">{s.result.error}</div>
              )}
              {s.result?.success && s.result?.output && (
                <pre className="result-step-output">{JSON.stringify(s.result.output, null, 2)}</pre>
              )}
            </div>
          ))}
        </div>
      )}

      {activeTab === 'output' && (
        <pre className="result-output-pre">{JSON.stringify(result.output, null, 2)}</pre>
      )}

      {activeTab === 'raw' && (
        <pre className="result-output-pre">{JSON.stringify(result, null, 2)}</pre>
      )}
    </div>
  );
}

// ─── PipelineGuide ───────────────────────────────────────────────────────────

function PipelineGuide() {
  const [open, setOpen] = useState(false);
  return (
    <div className="pipeline-guide">
      <button className="pipeline-guide-toggle" onClick={() => setOpen(o => !o)}>
        <i className={`fas fa-${open ? 'chevron-up' : 'circle-question'}`} />
        {open ? 'Hide' : 'How waterfalls work'}
      </button>
      {open && (
        <div className="pipeline-guide-body">
          <div className="guide-section">
            <h4><i className="fas fa-diagram-project" /> What is a Waterfall?</h4>
            <p>
              A waterfall is an ordered chain of <strong>tool steps</strong> that run one after another.
              Each step receives input, runs its tool, and passes output to the next step.
              You control exactly what each step receives using <strong>input mappings</strong>.
            </p>
          </div>

          <div className="guide-section">
            <h4><i className="fas fa-link" /> Variable Mapping (Templates)</h4>
            <p>
              Each step can receive a custom input built from any previous step's output or the original pipeline input.
              Use <code>{'{{source.field}}'}</code> placeholders in mapping values:
            </p>
            <table className="guide-table">
              <thead><tr><th>Template</th><th>Resolves to</th></tr></thead>
              <tbody>
                <tr><td><code>{'{{input.text}}'}</code></td><td>The <code>text</code> field of the original pipeline input</td></tr>
                <tr><td><code>{'{{input}}'}</code></td><td>The entire original input object</td></tr>
                <tr><td><code>{'{{step_0.result}}'}</code></td><td>The <code>result</code> field from Step 1's output</td></tr>
                <tr><td><code>{'{{step_2.summary}}'}</code></td><td>The <code>summary</code> field from Step 3's output</td></tr>
              </tbody>
            </table>
            <p className="guide-note">
              <i className="fas fa-lightbulb" /> Step indices in templates are zero-based and match the <code>order</code> saved with each step.
              If no mapping is defined, the previous step's full output is passed unchanged.
            </p>
          </div>

          <div className="guide-section">
            <h4><i className="fas fa-code-branch" /> Conditional Branching</h4>
            <p>
              Steps that use a <strong>conditional</strong> tool type can fork execution based on a true/false result.
              Set <em>If true</em> and <em>If false</em> to jump to any other step — or leave them empty to continue
              sequentially. This lets you build loops, skip logic, and multi-path workflows.
            </p>
            <p className="guide-note">
              <i className="fas fa-triangle-exclamation" /> Cycles are allowed but execution stops after 100 iterations to prevent infinite loops.
            </p>
          </div>

          <div className="guide-section">
            <h4><i className="fas fa-triangle-exclamation" /> Error Handling</h4>
            <p>Each step has an <em>On Error</em> policy:</p>
            <ul>
              <li><strong>Stop</strong> — halt the whole pipeline and return an error</li>
              <li><strong>Skip</strong> — skip this step and continue with the next</li>
              <li><strong>Retry</strong> — retry once; stop if it fails again</li>
            </ul>
          </div>

          <div className="guide-section">
            <h4><i className="fas fa-terminal" /> Calling via API</h4>
            <p>Once saved, execute any waterfall by its short name:</p>
            <pre><code>{`POST /api/waterfalls/execute/<short_name>/
Authorization: Bearer sk-your-api-key

{
  "input_data": { "text": "Process this", "url": "https://..." }
}`}</code></pre>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── WaterfallBuilder ────────────────────────────────────────────────────────

const WaterfallBuilder = ({ onNavigateToTool }) => {
  const [waterfalls, setWaterfalls] = useState([]);
  const [toolInstances, setToolInstances] = useState([]);
  const [toolTypeMetadata, setToolTypeMetadata] = useState({});
  const [selectedWaterfall, setSelectedWaterfall] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '', short_name: '', description: '', tags: '',
    is_favorite: false, is_active: true,
  });
  const [steps, setSteps] = useState([]);
  const [expandedSteps, setExpandedSteps] = useState(new Set());
  const [error, setError] = useState('');
  const [testInput, setTestInput] = useState('{}');
  const [testResult, setTestResult] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [toolSearch, setToolSearch] = useState('');
  const [runningWaterfall, setRunningWaterfall] = useState(null);
  const [runInput, setRunInput] = useState('{}');
  const [waterfallSearch, setWaterfallSearch] = useState('');

  useEffect(() => {
    fetchWaterfalls();
    fetchToolInstances();
    fetchToolTypeMetadata();
  }, []);

  const fetchWaterfalls = async () => {
    try {
      const response = await axios.get('/api/waterfalls/');
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setWaterfalls(data);
    } catch {
      setError('Failed to load waterfalls');
    }
  };

  const fetchToolInstances = async () => {
    try {
      const response = await axios.get('/api/tool-instances/');
      const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      setToolInstances(data);
    } catch {
      setError('Failed to load tool instances');
    }
  };

  const fetchToolTypeMetadata = async () => {
    try {
      const response = await axios.get('/api/tool-types/all_metadata/');
      const map = {};
      (response.data || []).forEach(t => { map[t.code] = t.output_schema || {}; });
      setToolTypeMetadata(map);
    } catch {
      // non-fatal
    }
  };

  const toggleExpanded = useCallback((index) => {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  }, []);

  const handleCreateNew = () => {
    setIsCreating(true);
    setSelectedWaterfall(null);
    setFormData({ name: '', short_name: '', description: '', tags: '', is_favorite: false, is_active: true });
    setSteps([]);
    setExpandedSteps(new Set());
    setError('');
    setTestResult(null);
  };

  const handleEdit = (waterfall) => {
    setSelectedWaterfall(waterfall);
    setIsCreating(true);
    setFormData({
      name: waterfall.name,
      short_name: waterfall.short_name,
      description: waterfall.description || '',
      tags: waterfall.tags || '',
      is_favorite: waterfall.is_favorite,
      is_active: waterfall.is_active,
    });
    setSteps(waterfall.steps.map(step => ({
      id: step.id,
      tool_instance: step.tool_instance,
      tool_instance_name: step.tool_instance_name,
      tool_type_name: step.tool_type_name,
      tool_type_code: step.tool_type_code,
      order: step.order,
      input_mapping: step.input_mapping || {},
      on_error: step.on_error,
      branch_true: step.branch_true ?? null,
      branch_false: step.branch_false ?? null,
    })));
    setExpandedSteps(new Set());
    setError('');
    setTestResult(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const stepsData = steps.map((step, index) => ({
        tool_instance: step.tool_instance,
        order: index,
        input_mapping: step.input_mapping || {},
        on_error: step.on_error || 'stop',
        branch_true: step.branch_true ?? null,
        branch_false: step.branch_false ?? null,
      }));
      const data = { ...formData, steps: stepsData };
      if (selectedWaterfall) {
        await axios.put(`/api/waterfalls/${selectedWaterfall.id}/`, data);
      } else {
        await axios.post('/api/waterfalls/', data);
      }
      setIsCreating(false);
      setSelectedWaterfall(null);
      fetchWaterfalls();
    } catch (err) {
      setError(err.response?.data?.detail || JSON.stringify(err.response?.data) || 'Failed to save waterfall');
    }
  };

  const handleDelete = async (waterfallId) => {
    if (!confirm('Delete this waterfall?')) return;
    try {
      await axios.delete(`/api/waterfalls/${waterfallId}/`);
      fetchWaterfalls();
      if (selectedWaterfall?.id === waterfallId) { setIsCreating(false); setSelectedWaterfall(null); }
    } catch {
      setError('Failed to delete waterfall');
    }
  };

  const handleAddStep = (toolInstanceId) => {
    const tool = toolInstances.find(t => t.id === parseInt(toolInstanceId));
    if (!tool) return;
    const newIndex = steps.length;
    const newStep = {
      id: `temp-${Date.now()}`,
      tool_instance: tool.id,
      tool_instance_name: tool.name,
      tool_type_name: tool.tool_type_name,
      tool_type_code: tool.tool_type_code,
      order: newIndex,
      input_mapping: {},
      on_error: 'stop',
      branch_true: null,
      branch_false: null,
    };
    setSteps(prev => [...prev, newStep]);
    setExpandedSteps(prev => new Set([...prev, newIndex]));
  };

  const handleRemoveStep = (index) => {
    setSteps(prev => prev.filter((_, i) => i !== index));
    setExpandedSteps(prev => {
      const next = new Set();
      prev.forEach(i => { if (i < index) next.add(i); else if (i > index) next.add(i - 1); });
      return next;
    });
  };

  const handleStepChange = (index, field, value) => {
    setSteps(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = [...steps];
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setSteps(items.map((item, i) => ({ ...item, order: i })));
    // Remap expanded indices
    const src = result.source.index;
    const dst = result.destination.index;
    setExpandedSteps(prev => {
      const next = new Set();
      prev.forEach(i => {
        if (i === src) { next.add(dst); return; }
        if (src < dst) { if (i > src && i <= dst) next.add(i - 1); else next.add(i); }
        else { if (i >= dst && i < src) next.add(i + 1); else next.add(i); }
      });
      return next;
    });
  };

  const executeWaterfall = async (waterfallId, inputJson, setResult) => {
    setIsExecuting(true);
    setResult(null);
    try {
      const inputData = JSON.parse(inputJson);
      const response = await axios.post(`/api/waterfalls/${waterfallId}/execute/`, { input_data: inputData });
      setResult(response.data);
    } catch (err) {
      if (err.message?.includes('JSON')) {
        setError('Invalid JSON in input');
      } else {
        setResult(err.response?.data || { success: false, error: err.message });
      }
    } finally {
      setIsExecuting(false);
    }
  };

  const filteredWaterfalls = waterfalls.filter(w => {
    if (!waterfallSearch) return true;
    const q = waterfallSearch.toLowerCase();
    return w.name.toLowerCase().includes(q) || w.short_name.toLowerCase().includes(q)
      || w.description?.toLowerCase().includes(q) || w.tags?.toLowerCase().includes(q);
  });

  // ── render ──────────────────────────────────────────────────────────────

  return (
    <div className="waterfall-builder">

      {/* ── list view ── */}
      {!isCreating && (
        <>
          <div className="header">
            <h2>Waterfalls</h2>
            <button onClick={handleCreateNew} className="btn-primary">
              <i className="fas fa-plus" /> New Waterfall
            </button>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="list-header">
            <div className="search-container">
              <input type="text" className="search-input"
                placeholder="Search by name, short name, description…"
                value={waterfallSearch} onChange={e => setWaterfallSearch(e.target.value)} />
              {waterfallSearch && (
                <button className="search-clear" onClick={() => setWaterfallSearch('')}>
                  <i className="fas fa-times" />
                </button>
              )}
            </div>
          </div>

          <PipelineGuide />

          {filteredWaterfalls.length === 0 ? (
            <p className="empty-state">
              {waterfallSearch ? `No waterfalls match "${waterfallSearch}"` : 'No waterfalls yet. Create one to get started!'}
            </p>
          ) : (
            <div className="waterfall-grid">
              {filteredWaterfalls.map(waterfall => (
                <div key={waterfall.id} className="waterfall-card">
                  <div className="waterfall-header">
                    <h4>{waterfall.name}</h4>
                    <div className="waterfall-badges">
                      {waterfall.is_favorite && <span className="favorite-badge"><i className="fas fa-star" /></span>}
                      {!waterfall.is_active && <span className="inactive-badge">Inactive</span>}
                    </div>
                  </div>
                  <div className="waterfall-meta">
                    <span className="waterfall-short-name">{waterfall.short_name}</span>
                    <span className="waterfall-steps">{waterfall.step_count} step{waterfall.step_count !== 1 ? 's' : ''}</span>
                  </div>
                  {waterfall.description && <p className="waterfall-description">{waterfall.description}</p>}
                  {waterfall.tags && (
                    <div className="waterfall-tags">
                      {waterfall.tags.split(',').map((tag, i) => <span key={i} className="tag">{tag.trim()}</span>)}
                    </div>
                  )}
                  <div className="waterfall-actions">
                    <button onClick={() => { setRunningWaterfall(waterfall); setRunInput('{}'); setTestResult(null); }}
                      className="btn-small btn-run">
                      <i className="fas fa-play" /> Run
                    </button>
                    <button onClick={() => handleEdit(waterfall)} className="btn-small">Edit</button>
                    <button onClick={() => handleDelete(waterfall.id)} className="btn-small btn-danger">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── builder view ── */}
      {isCreating && (
        <div className="builder-panel">
          <div className="builder-topbar">
            <button type="button" className="btn-back" onClick={() => setIsCreating(false)}>
              <i className="fas fa-arrow-left" /> Waterfalls
            </button>
            <h3>{selectedWaterfall ? 'Edit Waterfall' : 'New Waterfall'}</h3>
          </div>

          {error && <div className="error-message">{error}</div>}

          <form onSubmit={handleSubmit}>
            {/* basic info */}
            <div className="form-section">
              <h4>Details</h4>
              <div className="form-row">
                <div className="form-group">
                  <label>Name *</label>
                  <input type="text" value={formData.name} required placeholder="Data Processing Pipeline"
                    onChange={e => setFormData({ ...formData, name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Short name (API) *</label>
                  <input type="text" value={formData.short_name} required placeholder="data_pipeline"
                    onChange={e => setFormData({ ...formData, short_name: e.target.value })} />
                  <span className="help-text">Used in <code>/api/waterfalls/execute/{'{short_name}'}/ </code></span>
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea rows="2" value={formData.description} placeholder="What does this waterfall do?"
                  onChange={e => setFormData({ ...formData, description: e.target.value })} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Tags</label>
                  <input type="text" value={formData.tags} placeholder="automation, webhook"
                    onChange={e => setFormData({ ...formData, tags: e.target.value })} />
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={formData.is_favorite}
                      onChange={e => setFormData({ ...formData, is_favorite: e.target.checked })} />
                    Favorite
                  </label>
                  <label className="checkbox-label">
                    <input type="checkbox" checked={formData.is_active}
                      onChange={e => setFormData({ ...formData, is_active: e.target.checked })} />
                    Active
                  </label>
                </div>
              </div>
            </div>

            {/* steps */}
            <div className="form-section">
              <div className="steps-section-header">
                <h4>Pipeline Steps</h4>
                {steps.length > 0 && (
                  <div className="steps-expand-controls">
                    <button type="button" className="btn-text"
                      onClick={() => setExpandedSteps(new Set(steps.map((_, i) => i)))}>
                      Expand all
                    </button>
                    <button type="button" className="btn-text"
                      onClick={() => setExpandedSteps(new Set())}>
                      Collapse all
                    </button>
                  </div>
                )}
              </div>

              <div className="add-step-control">
                <span className="add-step-label"><i className="fas fa-plus" /> Add tool:</span>
                <input type="text" placeholder="Search tools…" value={toolSearch}
                  onChange={e => setToolSearch(e.target.value)} className="tool-search-input" />
                <select onChange={e => { handleAddStep(e.target.value); e.target.value = ''; setToolSearch(''); }}
                  size="4" className="tool-select-list">
                  <option value="" disabled>Select a tool…</option>
                  {toolInstances
                    .filter(t => !toolSearch || t.name.toLowerCase().includes(toolSearch.toLowerCase())
                      || t.tool_type_name?.toLowerCase().includes(toolSearch.toLowerCase()))
                    .map(t => (
                      <option key={t.id} value={t.id}>{t.name} ({t.tool_type_name})</option>
                    ))}
                </select>
              </div>

              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="steps">
                  {(provided, snapshot) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}
                      className={`steps-container ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}>
                      {steps.length === 0 ? (
                        <div className="empty-steps">
                          <i className="fas fa-diagram-project" />
                          <p>No steps yet. Add tools above to build your pipeline.</p>
                        </div>
                      ) : (
                        steps.map((step, index) => (
                          <Draggable key={step.id || index} draggableId={String(step.id || index)} index={index}>
                            {(provided, snapshot) => (
                              <StepCard
                                step={step}
                                index={index}
                                steps={steps}
                                isExpanded={expandedSteps.has(index)}
                                onToggle={toggleExpanded}
                                onRemove={handleRemoveStep}
                                onChange={handleStepChange}
                                toolTypeMetadata={toolTypeMetadata}
                                provided={provided}
                                snapshot={snapshot}
                              />
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                <i className={`fas ${selectedWaterfall ? 'fa-floppy-disk' : 'fa-plus'}`} />
                {selectedWaterfall ? ' Update' : ' Create'} Waterfall
              </button>
              <button type="button" onClick={() => setIsCreating(false)} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>

          {/* inline test */}
          {selectedWaterfall && (
            <div className="test-section">
              <h4><i className="fas fa-flask" /> Test Waterfall</h4>
              <div className="test-hint">
                <p>
                  Provide the initial input as JSON. Your step mappings can reference any field here with
                  {' '}<code>{'{{input.key}}'}</code>, and later steps can reference earlier outputs with
                  {' '}<code>{'{{step_0.field}}'}</code>, <code>{'{{step_1.field}}'}</code>, etc.
                </p>
              </div>
              <div className="form-group">
                <label>Input JSON</label>
                <textarea value={testInput} onChange={e => setTestInput(e.target.value)}
                  rows="4" className="code-editor" placeholder='{"text": "Hello world"}' />
              </div>
              <button type="button" onClick={() => executeWaterfall(selectedWaterfall.id, testInput, setTestResult)}
                disabled={isExecuting} className="btn-primary">
                {isExecuting ? <><i className="fas fa-spinner fa-spin" /> Running…</> : <><i className="fas fa-play" /> Run Test</>}
              </button>
              <ResultViewer result={testResult} />
            </div>
          )}
        </div>
      )}

      {/* ── run modal ── */}
      {runningWaterfall && (
        <div className="modal-overlay" onClick={() => setRunningWaterfall(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><i className="fas fa-play" /> {runningWaterfall.name}</h3>
              <button className="btn-close" onClick={() => setRunningWaterfall(null)}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Input JSON</label>
                <textarea value={runInput} onChange={e => setRunInput(e.target.value)}
                  rows="5" className="code-editor"
                  placeholder='{"text": "Hello world", "url": "https://example.com"}' />
              </div>
              <button
                onClick={() => executeWaterfall(runningWaterfall.id, runInput,
                  r => { setTestResult(r); })}
                disabled={isExecuting} className="btn-primary">
                {isExecuting ? <><i className="fas fa-spinner fa-spin" /> Running…</> : <><i className="fas fa-play" /> Execute</>}
              </button>
              <ResultViewer result={testResult} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WaterfallBuilder;
